import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import type { Benefit } from "./types";

interface ProductBenefitsProps {
  productKey: string;
  benefits: Benefit[];
}

export function ProductBenefits({ productKey, benefits }: ProductBenefitsProps) {
  const { t } = useTranslation();

  return (
    <section className="py-12 lg:py-28 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
            {t(`productPages.${productKey}.benefits.title`)}
          </h2>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.titleKey}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex items-start gap-6 bg-card rounded-xl border border-border p-6 lg:p-8 hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 rounded-xl bg-coral/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-6 w-6 text-coral" />
              </div>
              <div>
                <h3 className="text-lg font-bold font-subheading text-foreground mb-1">
                  {t(benefit.titleKey)}
                </h3>
                <p className="text-muted-foreground font-body leading-relaxed">
                  {t(benefit.descriptionKey)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
